"use client"
import { useState } from "react"
import Section from "@aura-design/system/section"
import Grid from "@aura-design/system/grid"
import { Content, isFilled } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next"
import { PlayIcon } from "@radix-ui/react-icons"
import Button from "@aura-design/system/button"

import { videoEmbedString } from "@airplan/utils/data-view"

/**
 * Props for `FeatureTextWithImage`.
 */
export type FeatureTextWithImageProps =
  SliceComponentProps<Content.FeatureTextWithImageSlice>

/**
 * Component for "FeatureTextWithImage" Slices.
 */
const VideoIcon = ({ slice }: FeatureTextWithImageProps): JSX.Element => {
  const gridClassConnect: string[] = ["z-10", "relative"]
  const containerClassConnect: string[] = ["relative pb-0"]

  const [isPlaying, setIsPlaying] = useState(false)
  const isYoutube = slice.primary?.video?.provider_name === "YouTube"

  if (slice.primary.is_flip) {
    gridClassConnect.push("reverse")
  }

  return (
    <Section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      container="smesh"
      className={containerClassConnect.join(" ").trim()}
    >
      <Grid col="three" className={gridClassConnect.join(" ").trim()}>
        <div className="valign one">
          <div className="text-center ">
            <div className="text-center">
              {isFilled.image(slice.primary.icon_one) && (
                <PrismicNextImage field={slice.primary.icon_one} alt="" />
              )}
            </div>
            <div className="content">
              {isFilled.richText(slice.primary.content_one) && (
                <PrismicRichText field={slice.primary.content_one} />
              )}
            </div>
            <div>
              {isFilled.link(slice.primary.button_link) &&
                isFilled.keyText(slice.primary.button_label) && (
                  <PrismicNextLink field={slice.primary.button_link}>
                    <Button>{slice.primary.button_label}</Button>
                  </PrismicNextLink>
                )}
            </div>
          </div>
        </div>
        {isPlaying ? (
          <div className="span-2 two relative">
            {isFilled.embed(slice.primary.video_link) && (
              <div
                className="embed-yt-video-icon"
                dangerouslySetInnerHTML={{
                  __html: videoEmbedString(
                    slice.primary.video_link.html as string,
                  ),
                }}
              />
            )}
          </div>
        ) : (
          <div className="span-2 two relative">
            {isFilled.image(slice.primary.image) && (
              <>
                <PrismicNextImage
                  field={slice.primary.image}
                  className="hide-small object-cover"
                  imgixParams={{
                    txt: slice.primary.image?.copyright,
                  }}
                  fallbackAlt=""
                />
                <PrismicNextImage
                  field={slice.primary.image}
                  className="hide-medium hide-large object-cover"
                  imgixParams={{
                    width: 768,
                    ar: "9:16",
                    crop: ["left"],
                    fit: "crop",
                    txt: slice.primary.image?.copyright,
                  }}
                  fallbackAlt=""
                />
              </>
            )}
            {isFilled.embed(slice.primary.video_link) && (
              <div className="action">
                <div className="valign circle h-4 w-4 justify-center bg-light-gray">
                  <Button
                    mode="link"
                    className="circle p-0"
                    onClick={() => setIsPlaying(true)}
                  >
                    <PlayIcon className="icon h4 text-stale-gray" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Grid>
    </Section>
  )
}

export default VideoIcon
